import { $window, isAndroid, isIos, appVersion } from 'util/global';
import { appendGetParams } from 'util/urls';


/** @module */
const sharing = {};


const FACEBOOK_GRAPH_API_VERSION = 'v12.0';


/**
 * Open a popup window with the given url
 * @param {String} url
 * @param {Number} [width] - width of the new window
 * @param {Number} [height] - height of the new window
 */
function socialPopup(url, width = 550, height = 440) {
    const left = ($window.width() / 2) - (width / 2);
    const top = ($window.height() / 2) - (height / 2);
    let opts = `width=${width},height=${height}`;

    if (left > 0 && top > 0) {
        opts += `,left=${left},top=${top}`;
    }

    window.open(url, 'socialpopup', opts);
}


/**
 * Check if URL is already encoded and encode if not
 * @param {String} url
 * @return {String} encoded URL
 */
var getEncodedUrl = function (url) {
    return url.indexOf(encodeURIComponent('/')) === -1 ? encodeURIComponent(url) : url;
};

/**
 * Facebook sharing controller
 * @param {JQuery} $btn
 */
sharing.facebook = function ($btn) {
    $btn.on('click', function (e) {
        e.preventDefault();

        var fb = window.FB || null,
            data = $btn.data();

        var getParams = {
            utm_content: data.axId,
            ref: data.axId,
        };

        //Gif url is being set in app/solvables.js
        if (data.gifUrl && data.gifUrl.length > 0) {
            getParams.gif_url = getEncodedUrl(data.gifUrl);
        }
        if (data.ogType && data.ogType.length > 0) {
            getParams.og_type = encodeURIComponent(data.ogType);
        }
        if (data.ogSharingMethod && data.ogSharingMethod.length > 0) {
            getParams.og_sharing_method = encodeURIComponent(data.ogSharingMethod);
        }

        data.url = appendGetParams(data.url, getParams);

        const shareNative = () => {
            window.location = appendGetParams('share://facebook', {
                link: getEncodedUrl(data.url),
                caption: encodeURIComponent(data.title),
                elementId: data.axId,
            });
        };

        var shareNonNative = function() {
            var params;
            if (fb !== null) {
                params = {
                    method: data['method'],
                    display: 'popup'
                };
                switch (data['method']) {
                    case 'feed':
                        params['link'] = data['url'];
                        params['description'] = data['title'];
                        if (data['image']) {
                            params['picture'] = data['image'];
                        }
                        break;
                    case 'share_open_graph':
                        params['action_type'] = 'og.shares';
                        params['action_properties'] = JSON.stringify({
                            object: data['url'],
                            ref: data['axId']
                        });
                        break;
                }
                fb.ui(params);
            } else {
                var fbUrl = 'https://www.facebook.com/' + FACEBOOK_GRAPH_API_VERSION + '/dialog/' + data['method'];
                params = {
                    app_id: data['appId'],
                    display: 'popup',
                    redirect_uri: getEncodedUrl(url)
                };
                var shareUrl = getEncodedUrl(data['url']);
                switch (data['method']) {
                    case 'feed':
                        params['link'] = shareUrl;
                        params['description'] = data['title'];
                        if (data['image']) {
                            params['picture'] = encodeURIComponent(data['image']);
                        }
                        break;
                    case 'share_open_graph':
                        params['action_type'] = 'og.shares';
                        params['action_properties'] = JSON.stringify({
                            object: shareUrl,
                            ref: data['axId']
                        });
                        break;
                }
                const popupUrl = appendGetParams(fbUrl, params);
                socialPopup(popupUrl, undefined, 200);
            }
        };

        if (isAndroid && appVersion.isGreaterThanOrEqualTo('1.10.0')) {
            shareNative();
        } else if (isAndroid) {
            try {
                Android.shareFacebook(data.url);
            } catch (err) {
                shareNonNative();
            }
        } else if (isIos && appVersion.isGreaterThanOrEqualTo('0.9.2')) {
            shareNative();
        } else {
            shareNonNative();
        }
    });
};


/**
 * Twitter sharing controller
 * @param {JQuery} $btn
 */
sharing.twitter = ($btn) => {
    $btn.on('click', (e) => {
        e.preventDefault();
        const data = $btn.data();
        const url = 'https://twitter.com/intent/tweet';

        const params = {
            text: encodeURIComponent(data.title),
        };

        if (data.url) {
            params.url = getEncodedUrl(data.url);
        }

        if (data.via) {
            params.via = 'brilliantorg';
        }

        socialPopup(appendGetParams(url, params));
    });
};


/**
 * Remind sharing controller
 * @param {JQuery} $btn
 */
sharing.remind = function ($btn) {
    $btn.on('click', function (e) {
        e.preventDefault();
        var data = $btn.data();
        var encodedUrl = getEncodedUrl(data.url);
        var url = 'https://www.remind.com/v1/share'
                  + '?url=' + encodedUrl
                  + '&referrer=' + encodedUrl
                  + '&text=' + encodeURIComponent(data.title);

        socialPopup(url, 880, 600);
    });
};

/**
 * Pinterest sharing controller
 * @param {JQuery} $btn
 */
sharing.pinterest = function ($btn) {
    $btn.on('click', function (e) {
        e.preventDefault();
        var data = $btn.data(),
            url = 'https://pinterest.com/pin/create/button/'
                + '?url=' + getEncodedUrl(data.url)
                + '&media=' + encodeURIComponent(data.media)
                + '&description=' + encodeURIComponent(data.title) + ' | via Brilliant.org';

        socialPopup(url, 750, 320);
    });
};

/**
 * Enable WhatsApp button when supported
 * @param {JQuery} $btn
 */
sharing.whatsapp = function ($btn) {
    /**
     * This only works on iOS, app or browser (but not iPod touch, apparently?)
     * I borrowed the concept from whatsapp-sharing
     * @see https://github.com/kriskbx/whatsapp-sharing
     * @see https://github.com/kriskbx/whatsapp-sharing/blob/master/src/button.js#L23
     */
    var iosByUa = navigator.userAgent.match(/iPhone/i) && !navigator.userAgent.match(/iPod/i);

    // if iOS, un-disable the button
    if(iosByUa) {
        $btn.removeClass('disabled');

    // we don't need it anymore, and removing it to makes the :last-child selector work
    } else {
        $btn.parent().addClass('whatsapp-removed');
        $btn.remove();
    }
};


/**
 * Use share://email?opts instead of mailto://?opts for handling the email within the app
 * @param {JQuery} $btn
 */
sharing.nativeEmail = function ($btn) {
    $btn.on('click', (e) => {
        e.preventDefault();
        window.location = appendGetParams($btn.attr('href').replace('mailto:', 'share://email'), {
            elementId: $btn.data('ax-id'),
        });
    });
};


// close the window if it's coming from a Facebook redirect
if (window.location.href.indexOf('fb_close_window') !== -1) {
    window.self.close();
}


module.exports = sharing;
